import type { DataTableSort } from "@/components/shared/data-table/DataTable";
import { getBackendClient } from "@/lib/backend";
import { orderListPage } from "@/lib/db/list-order";
import { fetchListTotals } from "@/lib/db/list-totals";
import { cast, toPostgrestIlikeContains } from "@/lib/utils/query-helpers";
import type {
  CreateShippingPayload,
  ShippingCalendarRecord,
  ShippingRecord,
  ShippingStatus,
  UpdateShippingPayload,
} from "../types";

const backend = getBackendClient();

const SHIPPING_RECORD_SELECT = `
  id, uid, date, tracking_number, status, shipping_service, recipient, receiver_address, receiver_state,
  sender_name_company, sender_address, sender_phone, total_quantity, quantity_1, quantity_2, quantity_3,
  account_id_1, account_id_2, account_id_3, notes, created_at, parcel_registration_status, tracking_events,
  ai_extracted_shipping_label_details, shipping_label_link, box_id, box_weight, drop_off_location_id,
  origin, eta_start, eta_end, last_synced_at
`;

const SHIPPING_RECORD_FIELD_KEYS = [
  "tracking_number",
  "shipping_service",
  "recipient",
  "receiver_address",
  "receiver_state",
  "sender_name_company",
  "sender_address",
  "sender_phone",
  "shipping_label_link",
  "ai_extracted_shipping_label_details",
  "box_id",
  "box_weight",
  "drop_off_location_id",
  "account_id_1",
  "account_id_2",
  "account_id_3",
  "quantity_2",
  "quantity_3",
  "notes",
] as const satisfies readonly (keyof CreateShippingPayload)[];
type ShippingRecordFieldKey = (typeof SHIPPING_RECORD_FIELD_KEYS)[number];

function assignDefinedField(
  target: Record<string, unknown>,
  payload: CreateShippingPayload | UpdateShippingPayload,
  key: ShippingRecordFieldKey,
): void {
  const value = payload[key];
  if (value !== undefined) {
    target[key] = value;
  }
}

function assignShippingRecordFields(
  target: Record<string, unknown>,
  payload: CreateShippingPayload | UpdateShippingPayload,
): void {
  for (const key of SHIPPING_RECORD_FIELD_KEYS) {
    assignDefinedField(target, payload, key);
  }
  const quantity1 = payload.quantity_1 === undefined ? payload.total_quantity : payload.quantity_1;
  if (quantity1 !== undefined) {
    target.quantity_1 = quantity1;
  }
}

function buildShippingRecordInsert(payload: CreateShippingPayload): Record<string, unknown> {
  const insertPayload: Record<string, unknown> = {
    last_modified_by_source: "database",
    date: payload.date,
    status: payload.status,
  };
  assignShippingRecordFields(insertPayload, payload);
  return insertPayload;
}

function buildShippingRecordUpdate(payload: UpdateShippingPayload): Record<string, unknown> {
  const updates: Record<string, unknown> = {
    last_modified_by_source: "database",
  };
  if (payload.date !== undefined) {
    updates.date = payload.date;
  }
  if (payload.status !== undefined) {
    updates.status = payload.status;
  }
  assignShippingRecordFields(updates, payload);
  return updates;
}

function resolveShippingRecord(
  response: {
    data: unknown;
    error: { message: string } | null;
  },
  emptyMessage: string,
): ShippingRecord {
  if (response.error) {
    throw new Error(response.error.message);
  }
  if (!response.data) {
    throw new Error(emptyMessage);
  }
  return cast<ShippingRecord>(response.data);
}

/** Loads one shipping record with its related account, invoice, and purchase-order context. */
export async function getShippingRecord(id: string): Promise<ShippingRecord> {
  return resolveShippingRecord(
    await backend.data
      .from("gl_shipping_records")
      .select(SHIPPING_RECORD_SELECT)
      .eq("id", id)
      .maybeSingle(),
    "Shipping record not found.",
  );
}

export async function deleteShippingRecord(id: string): Promise<void> {
  const { error } = await backend.data.from("gl_shipping_records").delete().eq("id", id);

  if (error) {
    throw new Error(error.message);
  }
}

/** Runs `xan-parcel-sync` scoped to a single tracking number via its query param. */
export async function refreshParcelStatus(trackingNumber: string): Promise<{
  success: boolean;
  created: number;
  synced: number;
  skipped: number;
  errors: string[];
}> {
  const { data, error } = await backend.functions.invoke("xan-parcel-sync", {
    query: { tracking_number: trackingNumber },
  });

  if (error) {
    throw new Error(error.message);
  }

  return data as {
    success: boolean;
    created: number;
    synced: number;
    skipped: number;
    errors: string[];
  };
}

export interface ShippingRecordListParams {
  limit?: number;
  offset?: number;
  /** Matched against uid, tracking number and recipient. */
  search?: string;
  /** Column order, already validated against the list's allowed sort fields. */
  sort?: DataTableSort | null;
  /** Scope statuses; `null` or empty applies no status filter. */
  statuses?: readonly ShippingStatus[] | null;
}

const SHIPPING_TOTAL_FIELDS = ["row_count", "total_quantity", "total_weight"] as const;

export type ShippingListTotals = Record<(typeof SHIPPING_TOTAL_FIELDS)[number], number>;

/** Newest first, which is what an operator wants before they pick a column. */
const DEFAULT_SHIPPING_SORT: DataTableSort = {
  direction: "desc",
  field: "date",
};

/**
 * Free-text search over the `search_text` computed column: uid, tracking
 * number, recipient, carrier, receiver address and state, sender, drop-off and
 * status. Previously uid, tracking number and recipient only, so a parcel could
 * not be found by the address it was going to or by who sent it.
 *
 * The totals RPC filters with the same function, so the strip under the table
 * counts the parcels the table is listing.
 */
function searchFilter(search: string): string {
  return `search_text.ilike.${toPostgrestIlikeContains(search)}`;
}

/** Lists shipping records with scope, search, sort and pagination applied server-side. */
export async function getShippingRecords(
  params?: ShippingRecordListParams,
): Promise<ShippingRecord[]> {
  const limit = params?.limit ?? 30;
  const offset = Math.max(0, params?.offset ?? 0);
  const sort = params?.sort ?? DEFAULT_SHIPPING_SORT;

  // Every `date` on this table is NULL, so the default sort is a single
  // 1,741-row tie: `orderListPage`'s unique key is the only thing that makes a
  // page window mean anything here at all.
  let query = orderListPage(
    backend.data.from("gl_shipping_records").select(SHIPPING_RECORD_SELECT),
    { ascending: sort.direction === "asc", column: sort.field },
  ).range(offset, offset + limit - 1);

  if (params?.statuses?.length) {
    query = query.in("status", [...params.statuses]);
  }

  const search = params?.search?.trim();
  if (search) {
    query = query.or(searchFilter(search));
  }

  const { data, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  return cast<ShippingRecord[]>(data || []);
}

/**
 * Row count for one scope, used by the scope chips and the summary cards.
 * Deliberately ignores search: the chips describe the slices themselves, and a
 * count that moved with the search box would contradict the summary cards.
 */
export async function countShippingRecords(
  statuses?: readonly ShippingStatus[] | null,
): Promise<number> {
  let query = backend.data.from("gl_shipping_records").select("id", { count: "exact", head: true });

  if (statuses?.length) {
    query = query.in("status", [...statuses]);
  }

  const { count, error } = await query;

  if (error) {
    throw new Error(error.message);
  }

  return count ?? 0;
}

/**
 * Parcel count, unit count and total weight for the whole filtered set.
 *
 * Takes the same scope and search `getShippingRecords` takes. Not derived from
 * the rows: those are one page, and a weight total describing thirty of 1741
 * parcels is a wrong number that looks like a right one.
 */
export function getShippingTotals(
  params?: Pick<ShippingRecordListParams, "search" | "statuses">,
): Promise<ShippingListTotals> {
  return fetchListTotals(
    "shipping_list_totals",
    {
      p_search: params?.search?.trim() || null,
      p_statuses: params?.statuses?.length ? [...params.statuses] : null,
    },
    SHIPPING_TOTAL_FIELDS,
  );
}

/**
 * Rows one calendar fetch pulls.
 *
 * The calendar day is derived per record — `date` is NULL table-wide, and the
 * fallback lives inside the `tracking_events` JSONB array — so PostgREST cannot
 * express a month window and the bucketing happens client-side over a capped
 * read. The status scope still filters server-side.
 */
export const SHIPPING_CALENDAR_FETCH_CAP = 2000;

const SHIPPING_CALENDAR_SELECT = `${SHIPPING_RECORD_SELECT.trim()}, invoice_id_1, invoice_id_2, invoice_id_3`;

/** Shipping records for the calendar, including the invoice links it reads units from. */
export async function getShippingRecordsForCalendar(params?: {
  statuses?: readonly ShippingStatus[] | null;
}): Promise<ShippingCalendarRecord[]> {
  let query = backend.data.from("gl_shipping_records").select(SHIPPING_CALENDAR_SELECT);

  if (params?.statuses?.length) {
    query = query.in("status", [...params.statuses]);
  }

  // Ordered by `created_at`, not `date`: `date` is NULL table-wide, so it
  // would leave `id` alone deciding which rows survive the cap, and the
  // truncation notice's "most recent shipments" claim would be false.
  const { data, error } = await orderListPage(query, {
    ascending: false,
    column: "created_at",
  }).range(0, SHIPPING_CALENDAR_FETCH_CAP - 1);

  if (error) {
    throw new Error(error.message);
  }

  return cast<ShippingCalendarRecord[]>(data || []);
}

export interface InvoiceUnitTotal {
  invoice_id: string;
  units: number;
}

/**
 * Invoiced units per linked invoice, summed from `gl_invoice_lines.quantity`.
 *
 * This is the invoice's own total, which is not the same number as the parcel's
 * allocated `quantity_1..3`: an invoice can be shipped across several parcels.
 * The calendar labels the two separately so neither reads as the other.
 *
 * A linked invoice with no lines stays in the result at zero rather than
 * disappearing, so the count of listed invoices matches the count of links.
 */
export async function getInvoiceUnitTotals(
  invoiceIds: readonly string[],
): Promise<InvoiceUnitTotal[]> {
  const ids = [...new Set(invoiceIds.filter(Boolean))];
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_invoice_lines")
    .select("invoice_id, quantity")
    .in("invoice_id", ids);

  if (error) {
    throw new Error(error.message);
  }

  const totals = new Map<string, number>(ids.map((id) => [id, 0]));
  const rows = cast<{ invoice_id: string | null; quantity: number | null }[]>(data || []);
  for (const row of rows) {
    if (row.invoice_id !== null && totals.has(row.invoice_id)) {
      totals.set(row.invoice_id, (totals.get(row.invoice_id) ?? 0) + (row.quantity ?? 0));
    }
  }

  return [...totals].map(([invoice_id, units]) => ({ invoice_id, units }));
}

export interface ShippingAccountRef {
  id: string;
  name: string | null;
  uid: string | null;
}

/** Name and UID of each account a shipping record links to. */
export async function getShippingAccounts(
  accountIds: readonly string[],
): Promise<ShippingAccountRef[]> {
  const ids = [...new Set(accountIds.filter(Boolean))];
  if (ids.length === 0) {
    return [];
  }

  const { data, error } = await backend.data
    .from("gl_accounts")
    .select("id, name, uid")
    .in("id", ids);

  if (error) {
    throw new Error(error.message);
  }

  return cast<ShippingAccountRef[]>(data || []);
}

/**
 * Creates a shipping record from the wizard payload and returns the persisted row.
 */ export async function createShippingRecord(
  payload: CreateShippingPayload,
): Promise<ShippingRecord> {
  return resolveShippingRecord(
    await backend.data
      .from("gl_shipping_records")
      .insert(buildShippingRecordInsert(payload))
      .select(SHIPPING_RECORD_SELECT)
      .single(),
    "Shipping record was not created.",
  );
}

/**
 * Updates a shipping record while rejecting payloads without editable fields.
 */
export async function updateShippingRecord(
  id: string,
  payload: UpdateShippingPayload,
): Promise<ShippingRecord> {
  const updates = buildShippingRecordUpdate(payload);

  if (Object.keys(updates).length === 1) {
    throw new Error("No shipping fields to update.");
  }

  return resolveShippingRecord(
    await backend.data
      .from("gl_shipping_records")
      .update(updates)
      .eq("id", id)
      .select(SHIPPING_RECORD_SELECT)
      .maybeSingle(),
    "Shipping record not found.",
  );
}

export async function registerWithParcel(
  trackingNumber: string,
  carrierCode: string | null,
  description: string | undefined,
  recordId: string,
): Promise<{
  success: boolean;
  quota_remaining: number;
  status: string;
  reason?: string;
}> {
  const { data, error } = await backend.functions.invoke("xan-parcel-add", {
    body: {
      tracking_number: trackingNumber,
      carrier_code: carrierCode,
      description: description ?? null,
      shipping_record_id: recordId,
    },
  });

  if (error) {
    throw new Error(error.message);
  }

  return data as {
    success: boolean;
    quota_remaining: number;
    status: string;
    reason?: string;
  };
}

export async function getParcelQuotaStatus(): Promise<{
  daily_add_count: number;
  quota_reset_date: string | null;
}> {
  // InsForge typed client omits generic .from(); narrow at this call site only.
  const { data, error } = await (backend.data as any)
    .from("parcel_sync_state")
    .select("daily_add_count, quota_reset_date")
    .maybeSingle();

  if (error) {
    throw new Error(error.message);
  }

  return (data ?? { daily_add_count: 0, quota_reset_date: null }) as {
    daily_add_count: number;
    quota_reset_date: string | null;
  };
}
